import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from 'react-router-dom';
import i18n from "i18next";
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import logoWithName from "../../static/logo_white.svg";
import { useUsers } from "../../contexts/UsersContext";
import { checkIfTokenStillValid, logout } from "../../repositories/utils";
import { 
  StyledNavDropItem,
  StyledNavLink,
  StyledDropdownButton,
} from './NavBarMain.styled';

const useTokenValidation = () => {
  const [validToken, setValidToken] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      const token = await checkIfTokenStillValid();
      setValidToken(token);
    };
    validateToken();
  }, []);

  return validToken;
};

const LanguageDropdown = React.memo(() => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)} 
        className="p-2 text-xl rounded-md hover:bg-darkGreen transition-colors duration-200"
        aria-label="Change language"
      >
        <span className="flex items-center">
          {i18n.language === 'en' ? '🇬🇧' : '🇪🇸'}
          <svg 
            className={`w-4 h-4 ml-1 transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>
      {isOpen && (
        <div className="absolute top-full right-0 mt-2 bg-darkGreen rounded-md shadow-lg z-10 min-w-[140px] border border-[#1C2D1C] overflow-hidden transform origin-top-right transition-all duration-200">
          <button 
            onClick={() => {
              i18n.changeLanguage('en');
              setIsOpen(false);
            }}
            className="flex items-center w-full px-4 py-3 text-white hover:bg-lightGreen hover:text-darkGreen transition-colors duration-200"
          >
            <span className="text-xl mr-2">🇬🇧</span>
            <span className="font-medium">{t('english')}</span>
          </button>
          <div className="h-[1px] bg-gray-700"></div>
          <button 
            onClick={() => {
              i18n.changeLanguage('es');
              setIsOpen(false);
            }}
            className="flex items-center w-full px-4 py-3 text-white hover:bg-lightGreen hover:text-darkGreen transition-colors duration-200"
          >
            <span className="text-xl mr-2">🇪🇸</span>
            <span className="font-medium">{t('spanish')}</span>
          </button>
        </div>
      )}
    </div>
  );
});

LanguageDropdown.displayName = 'LanguageDropdown';

const NavDropdown = ({ title, children, isActive }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="relative group">
      <StyledDropdownButton
        onClick={() => setIsOpen(!isOpen)}
        active={isActive}
        isOpen={isOpen}
      >
        {title}
        <svg 
          className="dropdown-arrow"
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </StyledDropdownButton>
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 bg-darkGreen rounded-md shadow-lg z-10 min-w-[180px] border border-[#1C2D1C] overflow-hidden transform origin-top-left transition-all duration-200">
          {React.Children.map(children, (child, index) => (
            <>
              {index > 0 && <div className="h-[1px] bg-[#111E12]"></div>}
              {child}
            </>
          ))}
        </div>
      )}
    </div>
  );
};

const NavDropItem = ({ href, children, isActive, onClick }) => (
  <StyledNavDropItem
    href={href}
    onClick={onClick}
    isActive={isActive}
  >
    {children}
  </StyledNavDropItem>
);

const UserDropdown = ({ handleLogout }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 text-lg text-white hover:text-lightGreen rounded-md hover:bg-darkGreen transition-colors duration-200"
        aria-label="User menu"
      >
        <span className="flex items-center">
          <i className="fas fa-user"></i>
          <svg 
            className={`w-4 h-4 ml-1 transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>
      {isOpen && (
        <div className="absolute top-full right-0 mt-2 bg-darkGreen rounded-md shadow-lg z-10 min-w-[160px] border border-[#1C2D1C] overflow-hidden transform origin-top-right transition-all duration-200">
          <a 
            href="/invoices"
            className="flex items-center px-4 py-3 text-white hover:bg-lightGreen hover:text-darkGreen transition-colors duration-200"
          >
            <i className="fas fa-file-invoice-dollar mr-2"></i>
            <span className="font-medium">{t('invoices')}</span>
          </a>
          <div className="h-[1px] bg-[#111E12]"></div>
          <button 
            onClick={() => {
              handleLogout();
              setIsOpen(false);
            }}
            className="flex items-center w-full px-4 py-3 text-white hover:bg-logoutRed hover:text-white transition-colors duration-200"
          >
            <i className="fas fa-sign-out-alt mr-2"></i>
            <span className="font-medium">Logout</span>
          </button>
        </div>
      )}
    </div>
  );
};

export const NavBarMain = ({ currentPage, type, history }) => {
  const { t } = useTranslation();
  const { users, userInfo } = useUsers();
  const validToken = useTokenValidation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const handleLogout = useCallback(() => logout(history), [history]);
  const navLinks = useMemo(() => (
    <>
      {currentPage !== "globalHome" && (
        <StyledNavLink 
          href="/home/logged" 
          active={currentPage === "home"}
        >
          <i className="fas fa-home"></i> {t('home')}
        </StyledNavLink>
      )}
      <NavDropdown 
        title={<><i className="fas fa-play"></i>&nbsp; Live</>}
        isActive={currentPage === "arbs" || currentPage === "valuebets"}
      >
        <NavDropItem href="/arbs/live" isActive={currentPage === "arbs"}>
          Arbs
        </NavDropItem>
        <NavDropItem href="/valuebets/live" isActive={currentPage === "valuebets"}>
          Value Bets
        </NavDropItem>
      </NavDropdown>
      <StyledNavLink 
        href="/events" 
        active={currentPage === "events"}
      >
        <i className="fas fa-calendar"></i> {t('events')}
      </StyledNavLink>
      <StyledNavLink 
        href="/extension" 
        active={currentPage === "extension"}
      >
        <i className="fa-brands fa-chrome"></i> Extension
      </StyledNavLink>
      <StyledNavLink 
        href="/rewards" 
        active={currentPage === "rewards"}
      >
        <i className="fas fa-trophy"></i> {t('rewards')}
      </StyledNavLink>
    </>
  ), [currentPage, t]);

  return (
    <motion.nav 
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.75, ease: "easeOut" }}
      className={`w-full flex flex-col max-w-[1600px] min-h-[80px] mx-auto h-auto ${['arbs', 'pre', 'valuebets'].includes(currentPage) ? 'max-w-full' : ''} z-[1000]`}
    >
      {/* Main navigation container - improved alignment */}
      <div className="w-full flex items-center justify-between p-2 min-h-[80px]">
        {/* Logo and language selector */}
        <div className="flex items-center">
          <Link to="/home" className="mr-4">
            <img src={logoWithName} alt="BetSmarter Logo" className="h-[60px]" />
          </Link>
          <LanguageDropdown />
        </div>
        
        {/* Desktop navigation - aligned with logo */}
        <div className="hidden lg:flex items-center flex-1 justify-between ml-4">
          <div className="flex items-center space-x-1">
            {validToken && navLinks}
            
            {!validToken && (
              <StyledNavLink 
                href="/pricing" 
              >
                {t('pricing')}
              </StyledNavLink>
            )}
            
            <StyledNavLink 
              href="https://academia.betsmarter.app/" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <i className="fa fa-graduation-cap"></i> Academy
            </StyledNavLink>
            
            {validToken && users && users.length > 0 && (
              <NavDropdown 
                title={<><i className="fa-solid fa-robot"></i>&nbsp; Bot</>}
                isActive={currentPage === "bot"}
              >
                <NavDropItem href="/home/bot" isActive={type === 'dashboard'}>
                  {t('dashboard')}
                </NavDropItem>
                <NavDropItem href="/home/bot/stats" isActive={type === 'stats'}>
                  {t('stats')}
                </NavDropItem>
                <NavDropItem href="/bot/referidos" isActive={type === 'referral'}>
                  {t('referrals')}
                </NavDropItem>
              </NavDropdown>
            )}
          </div>
          
          {/* User menu & desktop auth buttons */}
          <div className="flex items-center">
            {currentPage === "globalHome" ? (
              <div className="flex gap-2">
                {validToken ? (
                  <a 
                    href="/home/logged"
                    className="px-4 py-2 bg-white text-darkText font-montserrat text-lg hover:bg-lightGreen rounded-md w-[140px] text-center !no-underline hover:!bg-lightGreen"
                    >
                    {t('home')}
                  </a>
                ) : (
                  <>
                    <a 
                      href="/register"
                      className="px-4 py-2 border !border-lightGreen text-lightGreen font-montserrat text-lg hover:!bg-darkGreen hover:text-white rounded-md w-[140px] text-center !no-underline"
                    >
                      {t('signup')}
                    </a>
                    <a 
                      href="/login"
                      className="px-4 py-2 bg-white text-darkText font-montserrat text-lg hover:bg-lightGreen rounded-md w-[140px] text-center !no-underline hover:!bg-lightGreen"
                    >
                      {t('login')}
                    </a>
                  </>
                )}
              </div>
            ) : (
              validToken && (
                <UserDropdown handleLogout={handleLogout} />
              )
            )}
          </div>
        </div>
        
        {/* Mobile menu button */}
        <div className="lg:hidden flex items-center gap-2">
          {currentPage === "globalHome" && (
            <div className="flex gap-2">
              <a 
                href={validToken ? "/home/logged" : "/login"}
                className="px-4 py-2 bg-white text-darkText font-montserrat text-lg hover:bg-lightGreen rounded-md w-[140px] text-center !no-underline hover:!bg-lightGreen"
                >
                {validToken ? t('home') : t('login')}
              </a>
            </div>
          )}
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="p-2 text-white"
            aria-label="Toggle menu"
          >
            <i className={`fas ${isMenuOpen ? 'fa-times' : 'fa-bars'}`}></i>
          </button>
        </div>
      </div>
      
      {/* Mobile navigation menu */}
      <div className={`${isMenuOpen ? 'flex' : 'hidden'} lg:hidden flex-col p-2`}>
        <div className="flex flex-col space-y-1">
          {validToken && navLinks}
          
          {!validToken && (
            <StyledNavLink 
              href="/pricing" 
            >
              <i className="fas fa-money-bill mr-1"></i> {t('pricing')}
            </StyledNavLink>
          )}
          
          <StyledNavLink 
            href="https://academia.betsmarter.app/" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <i className="fas fa-graduation-cap mr-1"></i> Academy
          </StyledNavLink>
          
          {validToken && users && users.length > 0 && (
            <NavDropdown 
              title={<><i className="fas fa-robot mr-1"></i> Bot</>}
              isActive={currentPage === "bot"}
            >
              <NavDropItem href="/home/bot" isActive={type === 'dashboard'}>
                {t('dashboard')}
              </NavDropItem>
              <NavDropItem href="/home/bot/stats" isActive={type === 'stats'}>
                {t('stats')}
              </NavDropItem>
              <NavDropItem href="/bot/referidos" isActive={type === 'referral'}>
                {t('referrals')}
              </NavDropItem>
            </NavDropdown>
          )}
          
          {validToken && (
            <>
              <hr className="border-white my-1" />
              <a 
                href="/invoices"
                className="px-4 py-2 font-montserrat text-lg font-medium text-white !no-underline hover:bg-lightGreen hover:text-[#111E12] hover:font-bold rounded-md"
              >
                <i className="fas fa-file-invoice-dollar mr-1"></i> {t('invoices')}
              </a>
              <button 
                onClick={handleLogout}
                className="w-full text-left px-4 py-2 font-montserrat text-lg font-medium text-white !no-underline hover:bg-logoutRed hover:text-white hover:font-bold rounded-md"
              >
                <i className="fas fa-sign-out-alt mr-1"></i> Logout
              </button>
            </>
          )}
        </div>
      </div>
    </motion.nav>
  );
}; 